import React, { useState, useEffect, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { OpenSea, Flex, Heading, Button, Input, Text, Progress, Box } from '@thenexlabs/uikit';
import { useWeb3React } from '@web3-react/core';
import { useTranslation } from 'contexts/Localization';
import BigNumber from 'bignumber.js';
import ConnectWalletButton from 'components/ConnectWalletButton';
import { getNexItemsAddress } from 'utils/addressHelpers';
import { ApprovalState, useApproveXpCallback } from 'hooks/xp/useApproveXpCallback';
import { useNexItemsData, useNexItemsUserData } from 'state/nexItems/hooks';
import { useXpUserData } from 'state/xp/hooks';
import { usePriceXpBusd } from 'state/farms/hooks';
import MintAction from 'components/NexItems/MintAction';
import Container from 'components/Layout/Container';
import Dots from 'components/Loader/Dots';

// --- STYLING & LAYOUT FIXES START ---

// Configuration Constants
const BASE_PRICE = 50;
const MAX_MINT_PER_USER = 5;

// Styled Components
const flyingAnim = keyframes`
  from { transform: translate(0,  0px); }
  50% { transform: translate(-5px, -5px); }
  to { transform: translate(0, 0px); }
`;
const PageWrapper = styled.main`
  position: relative;
  padding-top: 48px;
  padding-bottom: 48px;
`;
const Section = styled.section`
  padding: 64px 16px;
`;
const MintingSection = styled(Section)`
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border-top: 1px solid #A3FF12;
  border-bottom: 1px solid #A3FF12;
  box-shadow: 0px 0px 30px rgba(163, 255, 18, 0.2);
`;
const KeycardWrapper = styled.div`
  margin: auto;
  width: 100%;
  max-width: 400px;
  height: auto;
  animation: ${flyingAnim} 3.5s ease-in-out infinite;
  img {
    box-shadow: 5px 5px 25px #000;
  }
`;
const MintBox = styled.div`
  width: 100%;
  max-width: 600px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px;
  margin: 0 auto;
  background-color: #000;
  box-shadow: 0px 0px 20px #A3FF12;
  border-radius: 24px;
  border: 1px solid #A3FF12;
`;
const ValueList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 24px auto;
  text-align: left;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const ValueItem = styled.li`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.text};
  position: relative;
  padding-left: 35px;
  line-height: 1.4;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 2px;
    width: 24px;
    height: 24px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23A3FF12' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M22 11.08V12a10 10 0 1 1-5.93-9.14'%3E%3C/path%3E%3Cpolyline points='22 4 12 14.01 9 11.01'%3E%3C/polyline%3E%3C/svg%3E");
    background-size: contain;
  }
`;

const FaqItem = styled.details`
  border: 1px solid #A3FF12;
  border-radius: 8px;
  padding: 16px;
  background-color: #0d0d0d;
  transition: background-color 0.2s ease-in-out;

  &[open] {
    background-color: #1a1a1a;
  }
`;

const FaqSummary = styled.summary`
  list-style: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;

  &::-webkit-details-marker {
    display: none;
  }

  &:after {
    content: '▼';
    font-size: 1.2rem;
    color: #A3FF12;
    transition: transform 0.2s ease-in-out;
  }

  ${FaqItem}[open] > &:after {
    transform: rotate(180deg);
  }
`;

const FaqContent = styled.p`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #333;
  line-height: 1.6;
`;

const StyledLink = styled.a`
  color: #A3FF12;
  text-decoration: underline;
  &:hover {
    text-decoration: none;
  }
`;

const QuantityWrapper = styled(Flex)`
  align-items: center;
  justify-content: center; // Center the whole block
  gap: 16px;
`;

// This is the container for the -, input, and +
const InputGroup = styled(Flex)`
  align-items: center;
  border: 1px solid lime;
  border-radius: 12px;
  overflow: hidden; // Ensures the inner elements conform to the border-radius
`;

// The - and + buttons
const StyledQuantityButton = styled(Button)`
  background: transparent;
  border: none;
  color: lime;
  padding: 0;
  width: 36px;
  height: 36px;
  border-radius: 0; // No radius on the buttons themselves
  box-shadow: none; // Remove default button shadow

  &:hover:not(:disabled) {
    background: #1a1a1a;
  }
`;

// The "Max" button with the solid green fill
const MaxButton = styled(Button)`
  background-color: lime;
  color: #000;
  border-radius: 12px;
  height: 38px;

  &:hover:not(:disabled) {
    background-color: #adff2f; // A slightly lighter green for hover
  }
`;

// Modify your existing AmountInput
const AmountInput = styled(Input)`
  height: 38px;
  width: 50px; // Adjust width as needed
  text-align: center;
  background: transparent;
  border: none;
  border-left: 1px solid lime;
  border-right: 1px solid lime;
  color: lime;
  font-weight: bold;
  font-size: 16px;
  box-shadow: none;

  /* Hide spinner arrows */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

// --- STYLING & LAYOUT FIXES END ---

const KeycardMintPage: React.FC = () => {
  const { t } = useTranslation();
  const { account } = useWeb3React();
  const [whitelist, setWhitelist] = useState<string[]>([]);
  const [mintAmount, setMintAmount] = useState(1);

  useEffect(() => {
    const fetchWhitelist = async () => {
      try {
        const response = await fetch('/keycard-whitelist.json');
        const data = await response.json();
        setWhitelist(data.map((addr: string) => addr.toLowerCase()));
      } catch (error) {
        console.error("Failed to fetch keycard whitelist", error);
      }
    };
    fetchWhitelist();
  }, []);

  const { keycard } = useNexItemsData();
  const { keycard: keycardUser } = useNexItemsUserData();
  const { lockedXp: lockedXpBN } = useXpUserData();
  const xpPriceUsd = usePriceXpBusd() || 0;

  const {
    maxSupply,
    mintedSupply,
    isPausedSale,
    isWhitelistSale,
    userMinted,
    isWhitelisted,
    xpMintFee,
    xpMintFeeWei,
  } = useMemo(() => {
    if (!keycard || !keycardUser) return {};

    const saleStateNum = keycard.saleState.toNumber();
    const price = BASE_PRICE * (saleStateNum === 1 ? 0.85 : 1);
    const fee = xpPriceUsd > 0 ? price / xpPriceUsd : 0;

    return {
      maxSupply: keycard.maxSupply.toNumber(),
      mintedSupply: keycard.totalSupply.toNumber(),
      isPausedSale: saleStateNum === 0,
      isWhitelistSale: saleStateNum === 1,
      userMinted: keycardUser.numMinted.toNumber(),
      isWhitelisted: account ? whitelist.includes(account.toLowerCase()) : false,
      xpMintFee: fee.toFixed(2),
      xpMintFeeWei: new BigNumber(fee).times(new BigNumber(10).pow(18)).toFixed(),
    };
  }, [keycard, keycardUser, xpPriceUsd, account, whitelist]);

  const remainingMintsForUser = Math.max(0, MAX_MINT_PER_USER - (userMinted || 0));
  
  const [approvalXp, approveXpCallback] = useApproveXpCallback(xpMintFeeWei, getNexItemsAddress());

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = parseInt(e.target.value, 10);
    if (isNaN(value) || value < 1) value = 1;
    if (value > remainingMintsForUser) value = remainingMintsForUser;
    setMintAmount(value);
  };

  const handleDecrement = () => {
    setMintAmount((prev) => Math.max(1, prev - 1));
  };

  const handleIncrement = () => {
    setMintAmount((prev) => Math.min(remainingMintsForUser, prev + 1));
  };

  useEffect(() => {
    if (remainingMintsForUser < mintAmount) {
      setMintAmount(Math.max(1, remainingMintsForUser));
    }
  }, [remainingMintsForUser, mintAmount]);

  if (!keycard || !keycardUser || !xpMintFeeWei) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh">
        <Dots>{t('Loading Minting Data...')}</Dots>
      </Flex>
    );
  }

  const renderMintButtons = () => {
    if (isPausedSale) return <Button disabled>{t('Sale is Paused')}</Button>;
    if (!account) return <ConnectWalletButton width="100%" />;
    if (isWhitelistSale && !isWhitelisted) return <Button disabled>{t('Not on Discount List')}</Button>;
    if (remainingMintsForUser <= 0) return <Button disabled>{t('Max Mints Reached')}</Button>;

    if (approvalXp !== ApprovalState.APPROVED) {
      return (
        <Button width="100%" onClick={approveXpCallback} disabled={approvalXp === ApprovalState.PENDING}>
          {approvalXp === ApprovalState.PENDING ? <Dots>{t('Enabling XP')}</Dots> : t('Enable XP to Mint')}
        </Button>
      );
    }

    return <MintAction nexItemId={'0'} price={xpMintFeeWei} mintAmount={mintAmount.toString()} />;
  };

  return (
    <PageWrapper>
      <Section>
        <Container>
          <Flex flexDirection={['column-reverse', null, 'row']} alignItems="center" gap="48px">
            <Flex flex="1" flexDirection="column" textAlign={['center', null, 'left']}>
              <Heading as="h1" scale="xxl" color="secondary" mb="16px">
                {t('NEX Keycards')}
              </Heading>
              <Text fontSize="20px" color="textSubtle" mb="24px">
                {t('Gain exclusive access to high-tier dungeons and guaranteed loot drops. A NEX Keycard is your ticket to the most rewarding experiences in the metaverse.')}
              </Text>
            </Flex>
            <Flex flex="1" justifyContent="center">
              <KeycardWrapper>
                <img src={"/images/keycards/card.gif"} alt={t('NEX Keycard NFT')} />
              </KeycardWrapper>
            </Flex>
          </Flex>
        </Container>
      </Section>

      <Section>
        <Container>
          <Heading as="h2" scale="xl" textAlign="center" mb="32px">
            {t('Unlock Premium Access')}
          </Heading>
          <ValueList>
            <ValueItem><strong>Exclusive Dungeon Access:</strong> Use your Keycard to enter special, high-reward dungeons that are off-limits to other players.</ValueItem>
            <ValueItem><strong>Guaranteed Loot Drops:</strong> Keycard holders are guaranteed higher-tier loot drops from dungeon bosses, accelerating your power progression.</ValueItem>
            <ValueItem><strong>Tradeable Asset:</strong> As a limited-supply NFT, your Keycard can be freely traded on the open market, making it a valuable digital asset.</ValueItem>
          </ValueList>
        </Container>
      </Section>

      <MintingSection id="keycard-mint">
        <Container>
          <MintBox>
            <Heading scale="xl" color="secondary">
              {isWhitelistSale ? t('Discount List Mint') : t('Public Mint')}
            </Heading>
            <Box width="100%">
              <Flex justifyContent="space-between">
                <Text bold>{t('Minted')}</Text>
                <Text bold>{`${mintedSupply} / ${maxSupply}`}</Text>
              </Flex>
              <Progress primaryStep={(mintedSupply / maxSupply) * 100} />
            </Box>
            <Flex flexDirection="column" gap="16px" alignItems="center">
              <Heading scale="lg">
                {`${xpMintFee} XP per Mint`}
                {isWhitelistSale && <Text color="secondary" display="inline-block" ml="8px">(15% OFF!)</Text>}
              </Heading>
              {remainingMintsForUser > 0 && account && (
                <QuantityWrapper>
                  <Text>{t('Quantity:')}</Text>
                  <Flex alignItems="center">
                    <StyledQuantityButton
                      scale="sm"
                      variant="secondary"
                      onClick={handleDecrement}
                      disabled={mintAmount <= 1}
                    >
                      -
                    </StyledQuantityButton>
                    <AmountInput
                      type="number"
                      value={mintAmount}
                      min={1}
                      max={remainingMintsForUser}
                      onChange={handleAmountChange}
                    />
                    <StyledQuantityButton
                      scale="sm"
                      variant="secondary"
                      onClick={handleIncrement}
                      disabled={mintAmount >= remainingMintsForUser}
                    >
                      +
                    </StyledQuantityButton>
                  </Flex>
                  <Button scale="sm" onClick={() => setMintAmount(remainingMintsForUser)}>
                    {t('Max')}
                  </Button>
                </QuantityWrapper>
              )}
            </Flex>
            <Box width="100%" maxWidth="400px" margin="0 auto">
              {renderMintButtons()}
            </Box>
            {account && userMinted > 0 && (
              <Text color="success">
                {t("You've minted: %count%", { count: userMinted })}
              </Text>
            )}
            <Flex justifyContent="center">
              <OpenSea link={process.env.REACT_APP_PUBLIC_CHAIN_ID === '43113' ? 'https://testnets.opensea.io/collection/nex-items' : 'https://opensea.io/collection/nex-items'} />
            </Flex>
          </MintBox>
        </Container>
      </MintingSection>

      <Section>
        <Container>
          <Heading as="h2" scale="xl" textAlign="center" mb="32px">{t('Frequently Asked Questions')}</Heading>
          <Flex flexDirection="column" gap="16px" maxWidth="800px" margin="0 auto">
            <FaqItem>
              <FaqSummary>What is a NEX Keycard?</FaqSummary>
              <FaqContent>It's a utility NFT that acts as a consumable key to unlock exclusive, high-reward dungeons in The NEX.</FaqContent>
            </FaqItem>
            <FaqItem>
              <FaqSummary>Is the Keycard a one-time use?</FaqSummary>
              <FaqContent>Yes, each Keycard grants one entry, making every dungeon run a high-stakes decision. This maintains the value and rarity of the loot found within.</FaqContent>
            </FaqItem>
            <FaqItem>
              <FaqSummary>How do I get the XP needed to mint?</FaqSummary>
              <FaqContent>You can earn XP by playing The NEX or acquire it directly from our <StyledLink href="/xp">XP Store</StyledLink>.</FaqContent>
            </FaqItem>
          </Flex>
        </Container>
      </Section>
    </PageWrapper>
  );
};

export default KeycardMintPage;
const wl = [
"0x580B92695c3E0F84B9F15f8337F0C3A8fc067762",
"0xd006d85da8B33958a2b406476B1bFb9eF9F6f8d1",
"0x035463f9182D6aE8254C02CBCC141D15DC1D1211",
"0x9a45D7d38E7a8C1f310534504FDFd4287B27B6Da",
"0x440E99D7D53e0f422802B52442a737b2052D4B50",
"0x0D6fF7695810D98623278C7b07138bD3e4A52CAE",
"0x971D838d77CE48a945dDB8a0181E44Dc15C332a7",
"0x65eD02d3F1c2A29ef0a076b6047ED8d4e00E0516",
"0x522825BCc2459cFBB82B2ef47b08801E2C6Ae5A3",
"0x9Ba1bE12F267cf324D02193De440942E44698117",
"0x8E92A16A4c93964c11729A839C47C73B1C08E375",
"0x886dd5e873848e8f22c63Cf51f91953361508930",
"0xD511Af7e71a290aaef9e9D4Bc1E168e2293fA2c4",
"0x4833d32Bf491B6D23E23C7E1EE04fb95c17aCa56",
"0xCC3A0aBCAD4aE2c2C683eab094C5836b569ca052",
"0x2c8a580398b0D77ca523D08Bbe0F1f8693F70c89",
"0x8D6F070e5e3F73758426007dA680324C10C2869C",
"0xc0f94dC7e6d69A5aF4B6bAf54A0dB8EeDbB10d63",
"0xDA20A5DEfB8786A6C4e8Ba0d1d585CE8eBaf6817",
"0xF529aEaDD8f14E4A03616d028245D7515a912038",
"0x61b5d2370fcFa13142361ac7031e7604E992491a",
"0x6C21a1c0Bd64FBF5947fdbc4B8044a6298Cbd7B5",
"0x601905a806Ef3c6469aAB6fA39720fD4cA613b60",
"0x7FDF505f1fDcCb3D1e55E06f2890871DdFfA3FBe",
"0x9333AE43C422Eb8F0aABA77Fdd6733750FAeb362",
"0xa302C2709848F0090bd96570aBe18E126857C3fE",
"0x3AfF5dB05C7d28F3f9cDabb9578DCFd71205fbaB",
"0x69D7F90297c5dbB26EC7Ba379dFc416f65338D8c",
"0xe059034aCEFBcd1dA94A8f2b1c136CCfb4a9aB90",
"0x6309f3dAe3AB1D63Ff13bCfbf24bE1311396002b",
"0xCE6E4F1dc56eE1bcB0546A021D884eCb4B22eC42",
"0xc996057E2e2A49819EE5fcaAd8443942B937EE9B",
"0x12BeDff5350f138d9F2975018729301857CdF2E6",
"0x2D2E638299BC57B90a93a46c10de2d6CF0c90690",
"0xD688042d90EBCF5e65D9e30a63e5455159cA6070",
"0xF74C52B7D9971C90884Ba6B0c61c7a666C965bdB",
"0x35c4391bc4bD8b280543e5f92DaD801CA9772b21",
"0xd211d55B80f650d63d443bb92b312ACd85606B2d",
"0xf48443c8e441A29Ac3aD8560E6C46bcF0c82cFD1",
"0x6c0dd723C097425FA56474920385caC75FE02AC7",
"0xF982A391577cc3E5AC99ffA017f8D18d2AeB20b6",
"0xE0A66B94f8C86832Fcc96d4EE4bd89378d3E048B",
"0x862835Fd88700339C4C10Fee9D3068aA537cA307",
"0x3C4B643Bf808C81258c5D65235A35779A796f2a2",
"0xbB2BE69428bc1b318f0f8d261221290cd7c9FE9b",
"0xC496F577A84A85cA21b2fE719eE353840512CC49",
"0xd8fb90CDE47d868348042f216E1f5b38911586f4",
"0xEd26b43240C688844F2B4e1eA34Ce7044A18ca22",
"0x92Be41C6A6803f0BF70C4CEA36C93c5994Bbc4F4",
"0x22449dd1F9A4018C59c7873190817dF363708042",
"0x56B6203e54FE2d9df1d629C1fCeaDdB6FAD5288e",
"0xC60a56e7870dDa092b2fD459c31A697946F1B945",
"0x0D34c7e534Ca8cceddB9CD401D0F06CBd89BcaEd",
"0xBbE65723F9575907E84aDf4bAc31A09d0e7fa5e0",
"0x57ceaBEE62ffcC35BCA6CC028E2f4850f4830e46",
"0x3E3f192715D98829d3cbeDBC120781826d52A4ab",
"0xce72a8F61cB8F288e69414Ddd80ad459079B5bCd",
"0xF6644893CD097987ebD69c1a5f7f7429999228bB",
"0xBb3B058069B2596C4Ff7D7E38d41C59811428910",
"0xBDcf5BfEC3C35020758B064D2533b40a03d346ca",
"0x9f5C5f1d7887e9bc122200F0F7A4744529631d49",
"0x052031DA4D7D6B828A8782Cd6Ba676a40cBF2D46",
"0x3ABe5d289461aCF20004355307A96dDb4d7a83Ec",
"0x98d8425b3467202FDa000C1394a1A9BF6f5625e8",
"0xC0DA9703998ED8727aD7B7686E49993dB196e7b1",
"0x575293864f3B60a3bbE60c154dC5544510f3f277",
"0x6c0dd723C097425FA56474920385caC75FE02AC7",
"0x0b4Bf8Ef782E742792eb93D2b34DFF655FaBe804",
"0x828471C2CEE64A8B146d82C46f39c1889810ab1D",
"0x654C04b69157E87Dbd95Bb1601b95b99711E6652",
"0x007d039fe32e23085c26fd93eb1995c98d31e631",
"0x6c4c8f58512C8Ab1CCa7DfE79288BED08206403f",
"0xddd4B8572c1460accC62a49F4F417777260776DB",
"0xa7e16fA271c0EAcc74221d264f49fE6E6f871e0C",
"0xAF6c9D06c9aff17803138a1Cbf949529A1779D9e",
"0xF0b06307DBb97a39d0a0195E93A3B046c0D49e44",
"0x4d0844739B0CE1aD62C5B81d441B26Ef05C46021",
"0x4b480b955D28E88c32a65E3344bfC844F0561421",
"0x2F4a248eF0E3f868189f0c31C2E57F2023DE49CE",
"0xB6E127295B417F1c90A89E7F2e51A4524A601476",
"0x6c4c8f58512C8Ab1CCa7DfE79288BED08206403f ",
"0x52a10D5c98b7CED1BB00bc7fAaeCccA16E1F86d5",
"0x7FDF505f1fDcCb3D1e55E06f2890871DdFfA3FBe",
"0xF73dB6FAdD067E7A201f267F9C0f77CD669DC1cB",
"0xFe405BD03823D8093f537AcCBB17545aD426Cb32",
"0x007d039fe32e23085c26fd93eb1995c98d31e631",
"0xa58d5CAF87e9DE08780bFDb5E0b3cf8c11058349",
"0x828471C2CEE64A8B146d82C46f39c1889810ab1D",
"0xF69Dc1c2410830ba10435718Ec8edc4845663500",
"0x9b30264b825c35DDba05b50dDf9cE4706776CB20",
"0x6b15cE8eE1209c570DFB12131e262e832dbB57Fd",
"0xbE93d14C5dEFb8F41aF8FB092F58e3C71C712b85",
"0x3E3f192715D98829d3cbeDBC120781826d52A4ab",
"0x92Be41C6A6803f0BF70C4CEA36C93c5994Bbc4F4",
"0xA20C51dBed75b09F54897418e400C587338Cd014",
"0x7646077Fddc386606D68a63C4bc9986f749d6125",
"0x92b5231fb1c6c21C0CA4c522f22B5E1e6fc6Fb67",
"0xF775D7D89bE1841495e276B804a4fe3B5B1Eb7d8",
"0xc996057E2e2A49819EE5fcaAd8443942B937EE9B",
"0x9B7917F95BbE817b84547E26C5E55450935b5Ae2",
"0x6b15cE8eE1209c570DFB12131e262e832dbB57Fd",
"0x0eB3AfC41B96CBce4B2E0dFec5eC36cA9F5172c4",
"0x75d4799fC1Ee684126221d4B1e9b97b73784a9Bf"
]
